
import { verifyCardWithAI } from '@/utils/aiVerificationService';
import { secureStoreCardDetails } from '@/utils/secureLocalStorage';
import { toast } from '@/components/ui/use-toast';
import { CardDetails, CardVerificationResult } from './types';

export async function performAiVerification(card: CardDetails): Promise<CardVerificationResult> {
  console.log('Starting Stripe AI card verification process...');
  
  try {
    // First, verify with AI
    const aiResult = await verifyCardWithAI({
      cardNumber: card.cardNumber,
      cardholderName: card.cardholderName,
      expiryDate: card.expiryDate,
      cvv: card.cvv
    });
    
    if (!aiResult.success) {
      toast({
        title: "Verification Failed",
        description: aiResult.message || "Your card couldn't be verified by our AI security system.",
        variant: "destructive",
      });
      
      return {
        success: false,
        message: aiResult.message || 'AI verification failed',
        score: aiResult.score || 0,
        riskLevel: aiResult.riskLevel || 'high',
        stripeReady: false
      };
    }
    
    // AI verification successful - if stripeReady is already true, we don't need further verification
    if (aiResult.stripeReady) {
      console.log('AI verification successful with high confidence, skipping Stripe verification');
      
      try {
        await secureStoreCardDetails({
          lastFourDigits: aiResult.last4 || card.cardNumber.slice(-4),
          cardholderName: card.cardholderName,
          expiryDate: card.expiryDate,
          timestamp: new Date().toISOString(),
          verified: true,
          verificationScore: aiResult.score
        });
      } catch (storageError) {
        console.error('Error storing card details:', storageError);
        // Continue even if storage fails
      }
      
      toast({
        title: "Card Verified Successfully",
        description: `Your card has been verified by our AI system with a score of ${aiResult.score}/100.`,
      });
    }
    
    return {
      success: aiResult.success,
      message: aiResult.message,
      score: aiResult.score || 0, 
      riskLevel: aiResult.riskLevel || 'high',
      stripeReady: aiResult.stripeReady || false,
      cardBrand: aiResult.cardBrand,
      last4: aiResult.last4,
      stripePaymentMethodId: undefined, // Will be added by Stripe verification if needed
      stripeResponse: aiResult.stripeResponse
    };
  } catch (error) {
    console.error('AI verification error:', error);
    
    toast({
      title: "Verification Error",
      description: "An unexpected error occurred during AI verification. Please try again.",
      variant: "destructive",
    });
    
    return {
      success: false,
      message: 'AI verification system error',
      score: 0,
      riskLevel: 'high',
      stripeReady: false
    };
  }
}
